import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/layout';
import {
  Button,
  Select,
  Card,
  Badge,
  getStatusBadgeVariant,
  Pagination,
  EntityLink,
  InsurerLogo,
  PolicyNumber,
  policyNumberLabel,
  CompactFilterBar,
  Table,
} from '@/components/common';
import type { Column, SortDirection } from '@/components/common/Table';
import { useRenewalQueue } from '@/hooks/useRenewals';
import { useAuthStore } from '@/store/auth';
import { useCan } from '@/hooks/useEmployeeRights';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import { useFilterParams } from '@/hooks/useFilterParams';
import {
  RENEWAL_PAYMENT_FILTER_OPTIONS,
  renewalPaymentStateLabel,
} from '@/pages/policies/renewalPaymentState';
import { formatDate, formatCurrency } from '@/utils';
import type {
  RenewalQueueFilters,
  RenewalQueuePolicyRef,
  RenewalQueueRow,
  RenewalQueueStatusFilter,
} from '@/types/renewalQueue';
// Queue #220/#246, carried over into B9, split further by B1/B3: Upcoming and
// Lapsed are now real tabs (each with its OWN Expiry Window vocabulary),
// translated into the policy-based queue's `bucket`/`days`/`lapsed_days`/
// `archived` wire shape rather than the old Renewal-table `days_to_expiry`.
// The Status list mirrors the queue's own `status` filter, including its
// `none` sentinel for a policy with no workflow row yet.
import {
  EXPIRY_WINDOW_LAPSED,
  EXPIRY_WINDOW_UPCOMING,
  lapsedWindowOptionsFor,
  renewalTabs,
  upcomingWindowOptions,
  windowToQueueParams,
  type RenewalTab,
} from './renewalExpiryWindow';
// B1: Month/Year, scoped to whichever tab is active -- see the module doc for
// why the option lists themselves depend on the tab and (for the current
// year) on each other.
import { monthOptionsFor, yearOptionsFor } from './renewalMonthYear';
import { RenewalTabList } from './RenewalTabList';
import { renewalStatusOptions, renewalWorkflowStatusLabel } from './renewalStatusOptions';

// The Month, Year, From Date and To Date controls were removed here (#220).
// Month and Year were sent as `expiry_month` / `expiry_year`, which the backend
// never read -- django-filter ignores an unknown param rather than rejecting
// it, so picking "March" narrowed nothing while looking like a working filter.
// From/To duplicated the Expiry Window in a less useful form.
//
// B1: Month and Year are back, this time reading `?month=`/`?year=` on the
// queue (`apps/renewals/queue.py::parse_month_year`/`apply_month_year`,
// proven by `backend/tests/test_b1_renewal_month_year.py`) -- not the old
// unread param names, and not restored until that backend support existed.
//
// B9: the list now reads `GET /renewals/queue/` (the policy table), not the
// Renewal workflow table. "Fetch Pending Renewals" materialised Renewal rows
// on demand -- the queue has no rows to materialise, membership comes
// straight from policy data, so the button, its hook and its service call are
// gone entirely rather than left as a no-op.

/**
 * Q-01 -- the filter state this page mirrors into the query string.
 *
 * Each tab keeps its OWN Expiry Window (B1/B3), so both are stored: flipping
 * to Lapsed and back must not lose what was picked on Upcoming, and that has
 * to survive a refresh too.
 *
 * DELIBERATELY NOT HERE: the page number. Every filter change resets it to 1,
 * so a restored number would be clobbered before the first paint.
 */
type RenewalPanelFilters = {
  search: string;
  /** '' means "never chosen" -> `EXPIRY_WINDOW_UPCOMING`. */
  tab: string;
  /** See WINDOW_ANY -- '' means "never chosen", NOT "no upper bound". */
  window_upcoming: string;
  window_lapsed: string;
  month: string;
  year: string;
  status: string;
  /** #345: the payment-link state (desk only), `RENEWAL_PAYMENT_FILTER_OPTIONS`. */
  pay: string;
  /** U-04. A queue ORDERING field name (see SORTABLE_FIELDS), not a column
   *  label; '' means "the queue's own default order". */
  sort: string;
  sort_dir: string;
};

const EMPTY_FILTERS: RenewalPanelFilters = {
  search: '',
  tab: '',
  window_upcoming: '',
  window_lapsed: '',
  month: '',
  year: '',
  status: '',
  pay: '',
  sort: '',
  sort_dir: '',
};

/**
 * Namespaced even though nothing links to `/renewals?…` today, so that adding
 * a deep link later cannot collide with a value the user chose. There is
 * therefore no `initial` seed and no `consumeSeedParams` here: `clearAll()`
 * empties everything this page owns, so Clear genuinely survives a refresh.
 */
const FILTER_PREFIX = 'f_';

/**
 * THE SENTINEL, and why it is not optional.
 *
 * `useFilterParams` DELETES an empty value from the query string rather than
 * writing `f_window_upcoming=`, and restores an absent key from `defaults`.
 * The namespace has no way to say "deliberately empty". The Upcoming tab's
 * "Not yet expired" option IS the empty string, and its app default is 30 days
 * -- so storing it as `''` would make the user's explicit "show me everything
 * not yet expired" come back as "next 30 days" on the very next refresh,
 * silently hiding a policy expiring in 45 days. That is queue #246's
 * hidden-window bug, restored by a round trip through the URL.
 *
 * So the UI's `''` is stored as `'any'`, which is non-empty and survives.
 *
 * The LAPSED tab needs no sentinel: its own default ("Already lapsed") is the
 * empty value, so `'' -> ''` is unambiguous there.
 */
const WINDOW_ANY = 'any';
/** The window the Upcoming tab opens on when nothing has been chosen. */
const UPCOMING_WINDOW_DEFAULT = '30';

const RENEWAL_TAB_VALUES = new Set<string>(renewalTabs.map((tab) => tab.id));
/** The non-empty Upcoming windows. `''` is handled by the sentinel above. */
const UPCOMING_WINDOW_VALUES = new Set(
  upcomingWindowOptions.map((option) => option.value).filter(Boolean)
);
const STATUS_VALUES = new Set(renewalStatusOptions.map((option) => option.value).filter(Boolean));

/** Stored value -> the Upcoming Select's value. */
function upcomingWindowFromStored(stored: string): string {
  if (stored === WINDOW_ANY) return '';
  if (UPCOMING_WINDOW_VALUES.has(stored)) return stored;
  // '' (nothing stored) and anything hand-edited fall back to the default.
  return UPCOMING_WINDOW_DEFAULT;
}

/** The Upcoming Select's value -> the stored value. */
function upcomingWindowToStored(value: string): string {
  return value === '' ? WINDOW_ANY : value;
}

/**
 * U-04: the queue fields `?ordering=` accepts, restricted to the ones this
 * page actually DISPLAYS.
 *
 * `RenewalQueueViewSet` sets `filter_backends = []`
 * (backend/apps/renewals/queue_views.py:55) -- which is NOT "this queue cannot
 * be ordered". It is the opposite: the queue parses every parameter by hand so
 * that an unusable one is a 400 instead of being silently dropped, and
 * `?ordering=` is one of them (`queue.parse_ordering`, called from
 * `build_queue`, against `queue.ORDERINGS`). That vocabulary is
 * `expiry_date` / `queue_date` / `current_premium`, each with a `-` form.
 * `queue_date` is not a column here, so it is left out: a sort the user cannot
 * see the effect of is not a control worth drawing.
 *
 * TWO TRAPS THIS PAGE MUST RESPECT, both different from DRF's OrderingFilter:
 *
 * 1. `parse_ordering` RAISES on an unknown value (queue.py:294), so the server
 *    answers 400 rather than falling back to the default order. A restored
 *    `?f_sort=` is therefore validated against this Set BEFORE it reaches
 *    `queryFilters` -- without that, one hand-edited character in the URL takes
 *    the whole list down instead of simply being ignored.
 * 2. `build_queue` passes `{} if customer` (queue.py:414), so a customer's
 *    `ordering` is discarded server-side. A sortable header would then be a
 *    control that fires a request and changes nothing -- exactly the defect
 *    this project keeps shipping -- so the sort is NOT offered to a customer at
 *    all (see `onSortChange` at the `Table` below).
 *
 * The column keys are the wire names on purpose: `Table` reports the activated
 * column's `key` to `onSortChange`, so anything else would need a lookup table
 * that could drift out of step with this Set.
 */
const SORTABLE_FIELDS = new Set(['expiry_date', 'current_premium']);

/** #345: the `?renewal_payment=` values the queue accepts (a junk one is a 400). */
const PAY_VALUES = new Set<string>(RENEWAL_PAYMENT_FILTER_OPTIONS.map((option) => option.value).filter(Boolean));

export function RenewalsListPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const isCustomer = user?.role === 'customer';
  // B4: the row action opens /policies/new, which needs policies Create.
  const canRecordRenewalPolicy = useCan('policies', 'create');
  const [page, setPage] = useState(1);

  // Q-01: the tab, both Expiry Windows, Month, Year, Status and the search
  // term, mirrored into `?f_*`. Restored SYNCHRONOUSLY in the hook's lazy
  // initializer, so the first request is already narrowed and the filter panel
  // is already open over it.
  const { filters, setFilters, clearAll } = useFilterParams<RenewalPanelFilters>({
    defaults: EMPTY_FILTERS,
    prefix: FILTER_PREFIX,
  });

  // Global rule 1 -- search-as-you-type without a request per keystroke:
  // `searchTerm` drives the Input synchronously, `debouncedTerm` (300ms) feeds
  // the queue's `search` filter. Seeded from `?f_search=`, so a refresh brings
  // the box back filled in and not just the results.
  const { term: searchTerm, debouncedTerm, setTerm: setSearchTerm } = useDebouncedSearch(filters.search);

  // ── The restored values, each validated against what this page can actually
  // offer. A hand-edited or stale URL must never leave a control reading one
  // thing while the request says another.
  const activeTab: RenewalTab = RENEWAL_TAB_VALUES.has(filters.tab)
    ? (filters.tab as RenewalTab)
    : EXPIRY_WINDOW_UPCOMING;

  const lapsedWindowValues = useMemo(
    () => new Set(lapsedWindowOptionsFor(user?.role).map((option) => option.value)),
    [user?.role]
  );
  const upcomingWindow = upcomingWindowFromStored(filters.window_upcoming);
  // A customer's stored 'all' falls back to "Already lapsed": `build_queue`
  // discards their `archived=` param, so the option is not offered to them and
  // a restored one must not linger as a selection with no matching option.
  const lapsedWindow = lapsedWindowValues.has(filters.window_lapsed) ? filters.window_lapsed : '';
  const activeWindow = activeTab === EXPIRY_WINDOW_LAPSED ? lapsedWindow : upcomingWindow;

  const yearOptions = useMemo(() => yearOptionsFor(activeTab), [activeTab]);
  const year = yearOptions.some((option) => option.value === filters.year) ? filters.year : '';
  const monthOptions = useMemo(() => monthOptionsFor(activeTab, year), [activeTab, year]);
  const month = monthOptions.some((option) => option.value === filters.month) ? filters.month : '';
  const status = STATUS_VALUES.has(filters.status) ? filters.status : '';
  // #345: a desk filter -- the queue ignores it for a customer, so it is never sent for one.
  const pay = !isCustomer && PAY_VALUES.has(filters.pay) ? filters.pay : '';
  // U-04. Validated against the Set for the reason in its doc comment: an
  // unknown `ordering` is a 400 from this endpoint, not a default order, so an
  // unrecognised `?f_sort=` must never reach `queryFilters`. A customer's
  // ordering is discarded server-side, so none is sent for them either --
  // better no ordering on the wire than one the header claims is applied.
  const sortKey = !isCustomer && SORTABLE_FIELDS.has(filters.sort) ? filters.sort : '';
  const sortDirection: SortDirection = filters.sort_dir === 'desc' ? 'desc' : 'asc';
  const ordering = sortKey ? `${sortDirection === 'desc' ? '-' : ''}${sortKey}` : undefined;

  // ...and write the rejection back, so the URL cannot keep a value the page
  // is ignoring. Without this, a month that became invalid (the tab changed,
  // or today moved past it) would sit in the query string reading as applied
  // and silently re-apply itself the moment the year changed back.
  useEffect(() => {
    const patch: Partial<RenewalPanelFilters> = {};
    if (filters.month !== month) patch.month = month;
    if (filters.year !== year) patch.year = year;
    if (filters.status !== status) patch.status = status;
    if (filters.pay !== pay) patch.pay = pay;
    if (filters.window_lapsed !== lapsedWindow) patch.window_lapsed = lapsedWindow;
    if (Object.keys(patch).length > 0) setFilters(patch);
  }, [
    filters.month,
    filters.year,
    filters.status,
    filters.pay,
    filters.window_lapsed,
    month,
    year,
    status,
    pay,
    lapsedWindow,
    setFilters,
  ]);

  // Push the debounced term into the filters and reset to page 1. Keyed off a
  // ref of the LAST TERM PUSHED (see the Policies pilot): seeded with the
  // restored value the mount pass is a no-op, and Clear is not immediately
  // undone by the 300ms of stale `debouncedTerm` still in flight.
  const pushedTermRef = useRef(filters.search);
  useEffect(() => {
    if (pushedTermRef.current === debouncedTerm) return;
    pushedTermRef.current = debouncedTerm;
    setFilters({ search: debouncedTerm });
    setPage(1);
  }, [debouncedTerm, setFilters]);

  // Switching tabs changes which Month/Year values are even offered (a future
  // year makes no sense once Lapsed is active) -- carrying a stale selection
  // across the switch would silently narrow the new tab by a value its own
  // Select no longer shows, so both are cleared with the tab.
  const handleTabChange = (tab: RenewalTab) => {
    setFilters({ tab, month: '', year: '' });
    setPage(1);
  };

  const handleWindowChange = (value: string) => {
    setFilters(
      activeTab === EXPIRY_WINDOW_LAPSED
        ? { window_lapsed: value }
        : { window_upcoming: upcomingWindowToStored(value) }
    );
    setPage(1);
  };

  const handleFilterChange = (key: 'status' | 'month' | 'year' | 'pay', value: string) => {
    setFilters({ [key]: value } as Partial<RenewalPanelFilters>);
    setPage(1);
  };

  // U-04: server-side sort. Nothing here reorders the rows in hand -- this
  // sends the queue's `?ordering=`, so the order is the whole filtered queue's
  // and not this page of twenty. Stored in the `f_` namespace with the filters
  // so a shared link reproduces the view the sender was looking at.
  const handleSortChange = (key: string, direction: SortDirection) => {
    setFilters({ sort: key, sort_dir: direction });
    setPage(1);
  };

  /**
   * The count that badges the Filter toggle and decides whether the panel
   * starts open.
   *
   * Counts ONLY the panel's own four controls. The tab is not one of them: it
   * is the page's scope, always visible above the bar with its own count, and
   * badging it would mean this page could never read as unfiltered. The search
   * box is always visible too, so it is excluded as everywhere else.
   *
   * The Expiry Window counts whenever it is NOT the empty option -- including
   * the Upcoming tab's 30-day default, which genuinely narrows the list. That
   * is what makes the panel open on the first paint, which is the property
   * this page's always-visible filter strip existed to guarantee: the strip
   * was made unconditional because an applied filter went invisible inside a
   * collapsing panel, and "Next 30 days" is an applied filter.
   *
   * The SORT is not counted either: it is a view preference, not a filter --
   * it narrows nothing, so badging it would say a filter is applied over a
   * list showing every row it showed before.
   */
  const activeFilterCount = [status, month, year, activeWindow, pay].filter(Boolean).length;

  const clearFilters = () => {
    setSearchTerm('');
    // The tab is scope, not a filter -- Clear must not throw the user from
    // Lapsed back to Upcoming. `clearAll` resets every owned key, so it is put
    // back in the same tick (the hook's setters read a ref, so the second call
    // merges onto the cleared state rather than a stale one).
    const keptTab = filters.tab;
    clearAll();
    setFilters({
      tab: keptTab,
      // `clearAll` resets this to '', which this page reads as "nothing
      // chosen" -> the 30-day default. Clear means NO window, so the sentinel
      // goes in explicitly.
      window_upcoming: WINDOW_ANY,
    });
    setPage(1);
  };

  const queryFilters: RenewalQueueFilters = useMemo(() => {
    return {
      ...windowToQueueParams(activeTab, activeWindow),
      month: month ? Number(month) : undefined,
      year: year ? Number(year) : undefined,
      status: (status || undefined) as RenewalQueueStatusFilter | undefined,
      renewal_payment: (pay || undefined) as RenewalQueueFilters['renewal_payment'],
      search: filters.search || undefined,
      ordering: ordering as RenewalQueueFilters['ordering'],
    };
  }, [activeTab, activeWindow, month, year, status, pay, filters.search, ordering]);

  const { data, isLoading, isError, refetch } = useRenewalQueue(page, queryFilters);
  const rows = useMemo(() => data?.results ?? [], [data]);
  const totalItems = data?.count ?? 0;
  const itemsPerPage = 20;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const handleRowClick = (row: RenewalQueueRow) => {
    navigate(`/renewals/${row.policy_id}`);
  };

  const getExpiryBadgeVariant = (days: number) => {
    if (days < 0) return 'error';
    if (days <= 7) return 'error';
    if (days <= 15) return 'warning';
    if (days <= 30) return 'info';
    return 'default';
  };

  /**
   * U-02: the columns, with a width each.
   *
   * SIZE = CONTENT + CELL PADDING. `Table` pads every `<td>` `px-6` (48px) and
   * sets `truncate` (`overflow: hidden`) under `layout="fixed"`, and cells are
   * border-box -- so a column declared at 8rem offers 80px of content and
   * anything wider is CLIPPED, with no scrollbar and no ellipsis. The four
   * narrow columns override the padding to `px-3` (24px) via `className` (`cn`
   * is tailwind-merge, so a `px-*` there beats the `px-6`):
   *
   *   policy         13rem  px-6 -> 160px  (an 18-digit insurer number: ~130px)
   *   customer       11rem  px-6 -> 128px
   *   product        11rem  px-6 -> 128px
   *   expiry          9rem  px-3 -> 120px  (a date over a "120 days ago" badge)
   *   premium        8.5rem px-3 -> 112px  (formatCurrency of a crore: ~95px)
   *   status          9rem  px-3 -> 120px
   *   actions        13rem  px-3 -> 184px  ("Record renewal policy" in an
   *                                         outline sm Button: ~164px)
   *                 -------
   *                  74.5rem = 1192px, inside the ~1424px a 1440-1536px desktop
   *   shows without a horizontal page scrollbar. THE ACTIONS COLUMN IS THE ONE
   *   THAT MATTERS: a clipped interactive control does not ellipsise, it simply
   *   ceases to exist -- unclickable, with nothing on screen to say it is
   *   there. At the 8rem an even split would have given it, the button was
   *   gone.
   *
   * EVERY COLUMN HERE RENDERS JSX, so not one of them gets a tooltip from
   * `Table` (it sets `title` only for string/number content, because
   * stringifying arbitrary JSX shows "[object Object]"). Each therefore carries
   * its own `title`. Both badges also take `max-w-full` with the label in a
   * `truncate` span: an `inline-flex` badge is an atomic inline-level box that
   * a `truncate` ancestor hard-CLIPS with no ellipsis.
   */
  const columns: Column<RenewalQueueRow>[] = [
    {
      key: 'policy',
      header: 'Policy Number',
      width: '13rem',
      // B2/B9: the insurer's policy number (falling back to our internal
      // number, computed server-side as `display_policy_number`), linked to the
      // policy detail page -- never a renewal reference number.
      // stopPropagation keeps the link's own navigation from being overridden
      // by the row's onClick, which goes to the queue detail page.
      render: (row: RenewalQueueRow) => (
        <div onClick={(e) => e.stopPropagation()}>
          <span className="block truncate" title={policyNumberLabel(row)}>
            <PolicyNumber
              source={row}
              render={(text) => (
                <EntityLink
                  kind="policy"
                  id={row.policy_id}
                  name={text}
                  className="text-sm font-medium text-primary-600 hover:underline dark:text-primary-400"
                />
              )}
            />
          </span>
          {/* U-05: the insurer's logo inside this cell, beside the name, as on
              the Policies list -- a column of its own would break the width
              budget above. `min-w-0` lets the name truncate. */}
          <span className="mt-0.5 flex items-center gap-1.5" title={row.insurer_name ?? '—'}>
            <InsurerLogo
              src={row.insurer_logo_url}
              name={row.insurer_name}
              description={row.insurer_description}
              size="xs"
            />
            <EntityLink
              kind="insurer"
              id={row.insurer}
              name={row.insurer_name ?? '—'}
              className="min-w-0 truncate text-xs text-gray-500 hover:underline dark:text-gray-400"
            />
          </span>
          {/* B9 review fix: the vehicle-rule hint. The queue only ever lists
              policies with no explicit successor (renewal_queue excludes linked
              ones), so a hint on a row always means "not linked yet". Never
              shown to a customer, who never receives the key at all. */}
          {row.possible_successor && <PossibleRenewalHint policyRef={row.possible_successor} />}
        </div>
      ),
    },
    {
      key: 'customer',
      header: 'Customer',
      width: '11rem',
      render: (row: RenewalQueueRow) => (
        <span className="block truncate" title={row.customer_name} onClick={(e) => e.stopPropagation()}>
          <EntityLink
            kind="contact"
            id={row.customer}
            name={row.customer_name}
            className="text-sm text-gray-900 hover:underline dark:text-white"
          />
        </span>
      ),
    },
    {
      key: 'product',
      header: 'Product',
      width: '11rem',
      render: (row: RenewalQueueRow) => (
        <p
          className="truncate text-sm text-gray-500 dark:text-gray-400"
          title={row.product_name ?? undefined}
        >
          {row.product_name}
        </p>
      ),
    },
    {
      key: 'expiry_date',
      header: 'Expiry',
      width: '9rem',
      className: 'px-3',
      // The key IS the wire name -- `Table` hands `column.key` straight to
      // `onSortChange`, which puts it on `?ordering=`.
      sortable: SORTABLE_FIELDS.has('expiry_date'),
      render: (row: RenewalQueueRow) => {
        const relative =
          row.days_to_expiry < 0
            ? `${Math.abs(row.days_to_expiry)} days ago`
            : `${row.days_to_expiry} days`;
        return (
          <div>
            <p className="truncate text-sm text-gray-900 dark:text-white" title={formatDate(row.expiry_date)}>
              {formatDate(row.expiry_date)}
            </p>
            <Badge variant={getExpiryBadgeVariant(row.days_to_expiry)} size="sm" className="max-w-full">
              <span className="truncate" title={relative}>
                {relative}
              </span>
            </Badge>
          </div>
        );
      },
    },
    {
      key: 'current_premium',
      header: 'Premium',
      width: '8.5rem',
      className: 'px-3',
      // `current_premium` on the wire orders by `gross_premium` server-side
      // (queue.ORDERINGS) -- the same number this column renders.
      sortable: SORTABLE_FIELDS.has('current_premium'),
      render: (row: RenewalQueueRow) => (
        <p
          className="truncate text-sm font-medium text-gray-900 dark:text-white"
          title={formatCurrency(row.current_premium)}
        >
          {formatCurrency(row.current_premium)}
        </p>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      width: '9rem',
      className: 'px-3',
      // workflow_status, not `status`: a row with no workflow is matched by the
      // "Not started" filter, so badging it "Pending" would contradict it.
      render: (row: RenewalQueueRow) => {
        const label = renewalWorkflowStatusLabel(row.workflow_status);
        return (
          <Badge
            variant={row.workflow_status ? getStatusBadgeVariant(row.workflow_status) : 'default'}
            size="sm"
            className="max-w-full"
          >
            <span className="truncate" title={label}>
              {label}
            </span>
          </Badge>
        );
      },
    },
    ...(isCustomer
      ? []
      : [
          {
            // #345: the customer's payment state on the insurer's link.
            key: 'renewal_payment',
            header: 'Pay link',
            width: '9rem',
            className: 'px-3',
            render: (row: RenewalQueueRow) => {
              const label = renewalPaymentStateLabel(row.renewal_payment?.state);
              if (!label || row.renewal_payment?.state === 'no_link') {
                return <span className="text-xs text-gray-400">—</span>;
              }
              return (
                <Badge variant={getStatusBadgeVariant(label.badge)} size="sm" className="max-w-full">
                  <span className="truncate" title={label.deskLabel}>
                    {label.deskLabel}
                  </span>
                </Badge>
              );
            },
          } as Column<RenewalQueueRow>,
          {
            key: 'actions',
            header: 'Actions',
            width: '13rem',
            className: 'px-3',
            render: (row: RenewalQueueRow) =>
              canRecordRenewalPolicy ? (
                <span className="inline-flex max-w-full" onClick={(e) => e.stopPropagation()}>
                  <Link to={`/policies/new?renewal=${row.policy_id}`}>
                    <Button variant="outline" size="sm">
                      Record renewal policy
                    </Button>
                  </Link>
                </span>
              ) : null,
          } as Column<RenewalQueueRow>,
        ]),
  ];

  return (
    <div>
      <PageHeader
        title="Renewals"
        subtitle={`${totalItems} policies for renewal`}
        breadcrumbs={[{ label: 'Renewals' }]}
      />

      {/* B1: Upcoming and Lapsed are real tabs, not two count badges that
          both wrote into one shared Expiry Window value -- a lapsed policy
          needs the opposite action from an upcoming one, and each gets its
          own Expiry Window vocabulary (B3). Cust-4/U-03 dropped the per-tab
          counts and made the selected tab unmistakable (RenewalTabList). */}
      <RenewalTabList activeTab={activeTab} onChange={handleTabChange} className="mb-4" />

      {/* The filters used to be an unconditional strip, because BEFORE that
          they sat behind a "Filters" button whose panel closed over them — an
          applied filter went invisible and the list looked wrong for no visible
          reason. CompactFilterBar cannot regress that: it opens itself whenever
          the active count goes 0 -> n (including on the first paint, which is
          why the Expiry Window has to be counted — see `activeFilterCount`),
          it never closes itself, and the badge and Clear stay on the collapsed
          bar so an applied filter is still announced even if the user folds the
          panel away themselves. */}
      <CompactFilterBar
        search={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Policy number or customer..."
        activeFilterCount={activeFilterCount}
        onClearFilters={clearFilters}
      >
        <Select
          label="Status"
          name="renewals-status"
          options={renewalStatusOptions}
          value={status}
          onChange={(e) => handleFilterChange('status', e.target.value)}
          placeholder="Status"
        />
        {/* B3: the option LIST itself is tab-scoped -- Lapsed never offers
            "Not yet expired" / "Next N days", Upcoming never offers
            "Already lapsed" / "Last N days" / "All Lapsed". Keying the
            Select on the active tab forces a remount on switch, so a value
            from the other tab's vocabulary (e.g. Lapsed's `'all'`) can
            never linger as a selection the new options list doesn't have.
            The Lapsed list is also ROLE-scoped: a customer is not offered
            "All Lapsed", whose only effect is `archived=all` -- a param
            `build_queue` discards for a customer, making the option an
            inert control that returns the identical list. */}
        <Select
          key={activeTab}
          label="Expiry Window"
          name="renewals-expiry-window"
          options={
            activeTab === EXPIRY_WINDOW_LAPSED
              ? lapsedWindowOptionsFor(user?.role)
              : upcomingWindowOptions
          }
          value={activeWindow}
          onChange={(e) => handleWindowChange(e.target.value)}
          placeholder="Expiry Window"
        />
        {/* B1: Month/Year narrow by calendar month/year of the date the
            desk works to (`queue_date`), independent of the Expiry Window
            above. Options are scoped to the active tab (`renewalMonthYear.ts`)
            so neither ever offers a value that tab cannot contain. */}
        <Select
          label="Month"
          name="renewals-month"
          options={[{ value: '', label: 'All Months' }, ...monthOptions]}
          value={month}
          onChange={(e) => handleFilterChange('month', e.target.value)}
          placeholder="Month"
        />
        <Select
          label="Year"
          name="renewals-year"
          options={[{ value: '', label: 'All Years' }, ...yearOptions]}
          value={year}
          onChange={(e) => handleFilterChange('year', e.target.value)}
          placeholder="Year"
        />
        {!isCustomer && (
          <Select
            label="Payment link"
            name="renewals-payment-link"
            options={RENEWAL_PAYMENT_FILTER_OPTIONS}
            value={pay}
            onChange={(e) => handleFilterChange('pay', e.target.value)}
            placeholder="Payment link"
          />
        )}
      </CompactFilterBar>

      {/* Three states, never two: a failed request tells us nothing about
          whether renewals exist, so it must not render as "No renewals found",
          which asserts something false about the book. */}
      {isLoading ? (
        <Table<RenewalQueueRow> data={[]} columns={columns} isLoading rowKey={(row) => row.id} />
      ) : isError ? (
        <Card padding="none">
          <ErrorState onRetry={() => refetch()} />
        </Card>
      ) : rows.length === 0 ? (
        <Card padding="none">
          <EmptyState />
        </Card>
      ) : (
        <>
          <Table<RenewalQueueRow>
            data={rows}
            columns={columns}
            rowKey={(row) => row.id}
            onRowClick={handleRowClick}
            // U-02: declared widths (no column jump when a tab or filter
            // changes which rows are on screen) and a header that stays put
            // while the rows scroll under it.
            layout="fixed"
            stickyHeader
            // U-04: Expiry and Premium are sort controls, and only for a
            // broker-side user. `build_queue` discards a customer's `ordering`
            // (queue.py:414), so withholding the handler is what keeps every
            // header plain text for them -- `Table` renders a `sortable` column
            // as inert without it -- rather than drawing a control that would
            // fire a request and change nothing.
            sortKey={sortKey}
            sortDirection={sortDirection}
            onSortChange={isCustomer ? undefined : handleSortChange}
          />
          {totalPages > 1 && (
            <Card padding="none" className="mt-4 overflow-hidden">
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={setPage}
              />
            </Card>
          )}
        </>
      )}
    </div>
  );
}

// B9 review fix: the vehicle-rule hint that keeps a real-world renewal
// findable until it is explicitly linked (three known pairs on prod). Shown
// only when the queue offered one (see the call site's comment) -- never a
// substitute for the explicit "Record renewal policy" action beside it.
function PossibleRenewalHint({ policyRef }: { policyRef: RenewalQueuePolicyRef }) {
  return (
    <Link
      to={`/policies/${policyRef.id}`}
      className="block truncate text-xs text-amber-700 hover:underline dark:text-amber-400"
      title={`Possible renewal: ${policyNumberLabel(policyRef)}`}
    >
      Possible renewal: {policyNumberLabel(policyRef)}
    </Link>
  );
}

function EmptyState() {
  return (
    <div className="text-center py-12">
      <h3 className="mt-4 text-sm font-semibold text-gray-900 dark:text-white">No renewals found</h3>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
        No policies are due for renewal with the selected filters.
      </p>
    </div>
  );
}

function ErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="text-center py-12" role="alert">
      <h3 className="mt-4 text-sm font-semibold text-red-700 dark:text-red-400">
        Couldn&apos;t load renewals.
      </h3>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
        That is not the same as there being none — please try again.
      </p>
      <Button variant="outline" className="mt-4" onClick={onRetry}>
        Try again
      </Button>
    </div>
  );
}
